import * as React from "react";
import parse from "html-react-parser";
import styled from "styled-components";
import isNull from "lodash/isNull";
import isUndefined from "lodash/isUndefined";

import { withJsonFormsControlProps } from "@jsonforms/react";
import { ControlElement, ControlProps } from "@jsonforms/core";

import SSRIcon from "@ingka/ssr-icon";
import informationCircle from "@ingka/ssr-icon/paths/information-circle";
import InputField from "@ingka/input-field";
import FormField from "@ingka/form-field";
import Accordion, { AccordionItem } from "@ingka/accordion";

import { VisibilityWrapper } from "../../styles";
import { findAndTransformHTMLString } from "../utils";

interface InputProps extends ControlProps {
	shouldFocus?: boolean;
	uischema: {
		subText?: string;
	} & ControlElement;
}

const StyledLabelWrapper = styled.div`
	display: flex;
	align-items: center;
	gap: 0.5rem;
`;

const StyledSSRIcon = styled(SSRIcon)`
	color: rgb(var(--colour-semantic-informative, 0, 88, 163));
`;

const StyledList = styled.ul`
	margin-top: 0rem;
`;

const InputControl: React.FC<InputProps> = ({
	data,
	schema,
	handleChange,
	path,
	label,
	description,
	uischema,
	errors,
	required,
	...props
}) => {
	const isInteger = schema?.type === "integer";

	const externalValue = React.useMemo(() => {
		if (!isNull(data) && !isUndefined(data)) {
			return String(data);
		}
		if (!isUndefined(schema?.default) && !isNull(schema?.default)) {
			return String(schema.default);
		}
		return "";
	}, [data, schema?.default]);

	const [draftValue, setDraftValue] = React.useState(externalValue);

	React.useEffect(() => {
		setDraftValue(externalValue);
	}, [externalValue]);

	const parsedSubText = React.useMemo(() => {
		if (!uischema?.subText) return null;
		return parse(findAndTransformHTMLString(uischema.subText));
	}, [uischema?.subText]);

	const showStyledDescription = React.useMemo(() => {
		return Boolean(uischema?.scope?.includes("subText"));
	}, [uischema?.scope]);

	const hasValue = React.useMemo(() => {
		return draftValue !== "";
	}, [draftValue]);

	const errMessage = React.useMemo(() => {
		if (required && errors.length) {
			return `${label} ${errors}`;
		}
		if (required) {
			return `${label} is a required property`;
		}
		return `${label} ${errors}`;
	}, [required, errors, label]);

	const isValid = React.useMemo(() => {
		return required ? errors.length === 0 && hasValue : errors.length === 0;
	}, [required, errors.length, hasValue]);

	const shouldValidate = React.useMemo(() => {
		return errors.length > 0 || (required && !hasValue);
	}, [errors.length, required, hasValue]);

	const fieldHelper = React.useMemo(
		() => ({
			msg: (
				<>
					{parsedSubText && (
						<>
							{parsedSubText}
							<br />
						</>
					)}
					{description && (
						<>
							{description}
							<br />
						</>
					)}
				</>
			),
			id: `input-control-msg-${path}`,
		}),
		[parsedSubText, description, path]
	);

	const validation = React.useMemo(
		() => ({
			id: `error-msg-id-${path}`,
			msg: errMessage,
			type: "error" as const,
		}),
		[path, errMessage]
	);

	const commitValue = React.useCallback(
		(value: string) => {
			if (isInteger) {
				if (value === "" || value === "-") {
					handleChange(path, value);
					return;
				}

				if (!/^-?\d+$/.test(value)) {
					return;
				}

				handleChange(path, parseInt(value, 10));
				return;
			}

			handleChange(path, value);
		},
		[isInteger, handleChange, path]
	);

	const onChange = React.useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const nextValue = e.target.value;

			if (isInteger) {
				if (nextValue === "" || nextValue === "-" || /^-?\d+$/.test(nextValue)) {
					setDraftValue(nextValue);
				}
				return;
			}

			setDraftValue(nextValue);
		},
		[isInteger]
	);

	const onBlur = React.useCallback(() => {
		commitValue(draftValue);
	}, [commitValue, draftValue]);

	return (
		<VisibilityWrapper visible={props.visible}>
			<FormField fieldHelper={fieldHelper} valid={isValid} shouldValidate={shouldValidate} validation={validation}>
				<InputField
					aria-label={label || "Text field"}
					id={path}
					label={label}
					type="text"
					onChange={onChange}
					onBlur={onBlur}
					value={draftValue}
					disabled={!props.enabled}
					inputMode={isInteger ? "numeric" : undefined}
				/>
			</FormField>

			{showStyledDescription && (
				<Accordion size="small">
					<AccordionItem
						id="styledDescription"
						subtle
						title={
							<StyledLabelWrapper>
								<StyledSSRIcon paths={informationCircle} />
								<span>Styled description</span>
							</StyledLabelWrapper>
						}
					>
						<StyledList>
							<li>
								*text*: <strong>text</strong>
							</li>
							<li>
								**text**: <i>text</i>
							</li>
							<li>
								__text__: <u>text</u>
							</li>
							<li>
								~~text~~: <s>text</s>
							</li>
							<li>
								<span>
									{"text <>new line"}: text
									<br />
									new line
								</span>
							</li>
							<li>
								[link name](link url): <a href="#">link name</a>
							</li>
							<li>
								[[text1; text2]]:{" "}
								<ul>
									<li>text1</li>
									<li>text2</li>
								</ul>
							</li>
						</StyledList>
					</AccordionItem>
				</Accordion>
			)}
		</VisibilityWrapper>
	);
};

export default withJsonFormsControlProps(InputControl);
